import * as React from 'react';
import { useTranslation } from 'react-i18next';
import classnames from '../../../../core/utils/classnames.tsx';
import DOMPurify from 'dompurify';
import { format } from 'date-fns';
import i18n from 'i18next';
import Frame from '../../../../core/components/Frame.tsx';
import { DARK_MODE_LOCAL_STORAGE_KEY } from '../../../../../App.tsx';
import { withIcon } from '../../../../core/components/Icon.tsx';
import { faArrowDownToBracket, faFile, faLanguage } from '@fortawesome/pro-regular-svg-icons';

import contentStyle from './email-content.css?inline';
import Button from '../../../../core/components/Button.tsx';

const LanguageIcon = withIcon(faLanguage);
const FileIcon = withIcon(faFile);
const DownloadIcon = withIcon(faArrowDownToBracket);

export const PURIFY_CONFIG = {
    ALLOWED_TAGS: [
        'address',
        'article',
        'aside',
        'footer',
        'header',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'hgroup',
        'main',
        'nav',
        'section',
        'blockquote',
        'dd',
        'div',
        'dl',
        'dt',
        'figcaption',
        'figure',
        'hr',
        'li',
        'main',
        'ol',
        'p',
        'pre',
        'ul',
        'a',
        'abbr',
        'b',
        'bdi',
        'bdo',
        'br',
        'cite',
        'code',
        'data',
        'dfn',
        'em',
        'i',
        'kbd',
        'mark',
        'q',
        'rb',
        'rp',
        'rt',
        'rtc',
        'ruby',
        's',
        'samp',
        'small',
        'span',
        'strong',
        'sub',
        'sup',
        'time',
        'u',
        'var',
        'wbr',
        'caption',
        'col',
        'colgroup',
        'table',
        'tbody',
        'td',
        'tfoot',
        'th',
        'thead',
        'tr',
        'img',
        'style',
        'html',
        'head',
        'meta',
        'comment',
    ],
    ALLOWED_ATTR: ['href', 'name', 'target', 'class', 'src', 'alt', 'width', 'height', 'id', 'style'],
    ALLOWED_URI_REGEXP:
        /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|<cid|xmpp):|[^a-z]|[^a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    WHOLE_DOCUMENT: true,
    SANITIZE_DOM: true,
};

export interface File {
    url?: string;
    type?: string;
    name?: string;
}

export interface EmailAttachment {
    cid?: string;
    contentDisposition?: string;
    url?: string;
    type?: string;
    contentType?: string;
    name?: string;
}

export interface Email {
    receivedAt?: Date;
    text?: string;
    html?: string;
    attachments?: EmailAttachment[];
}

export const useEmailBody = (email: Email) => {
    let attachmentsPerCid = Object.fromEntries(
        (email?.attachments || []).filter((a) => !!a.cid).map((a) => [a.cid, a])
    );
    const attachmentsWithoutCid = (email?.attachments || []).filter((a) => !a.cid);

    const plainText = document.createElement('pre');
    plainText.style.whiteSpace = 'pre-wrap';
    plainText.textContent = email.text || '';
    const emailDocument = DOMPurify.sanitize(email.html || plainText.outerHTML, {
        ...PURIFY_CONFIG,
        RETURN_DOM: true,
    }) as HTMLElement;
    for (const image of emailDocument.querySelectorAll('img[src]')) {
        const source = image.getAttribute('src') || '';
        if (!source.startsWith('cid:')) continue;
        const cid = `<${source.slice(4)}>`;
        const attachment = attachmentsPerCid[cid];
        // Attachment URLs are untrusted too; isValidAttribute applies the URI policy of the sanitize call above.
        if (!attachment?.url || !DOMPurify.isValidAttribute('img', 'src', attachment.url)) continue;
        image.setAttribute('src', attachment.url);
        delete attachmentsPerCid[cid];
    }
    const emailContent = emailDocument.outerHTML;

    const remainingAttachments = Object.values(attachmentsPerCid).concat(attachmentsWithoutCid);

    return {
        content: emailContent,
        attachments: remainingAttachments,
    };
};

const EmailPreview = ({ email, className, ...props }: any) => {
    const { t } = useTranslation('assistance');

    const isDarkMode = !!localStorage.getItem(DARK_MODE_LOCAL_STORAGE_KEY);

    const isHtml = !!email.html;
    const { content: emailContent } = useEmailBody(email);

    const date = email.receivedAt && new Date(email.receivedAt);

    return (
        <div className={classnames('flex flex-col gap-5 py-5', className)} {...props}>
            <div className="px-6 flex w-full">
                <div className="font-semibold text-base text-content-strong">{email?.subject}</div>
            </div>

            <div className="px-6">
                <Frame>
                    <style>{contentStyle}</style>
                    <div
                        className={classnames(
                            'email--html',
                            !isHtml && 'email--plain',
                            isDarkMode && 'email--dark-mode'
                        )}
                        dangerouslySetInnerHTML={{ __html: emailContent }}
                    />
                </Frame>
            </div>
        </div>
    );
};

export default EmailPreview;
